import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'

import http, { registerSessionProvider } from '@lib/http-client'
import { toUtcIsoString } from '@utils/date'

import {
  AuthCredentialsResponse,
  AuthDevice,
  AuthState,
  FlipperAuthError,
  LoginCredentials,
  LoginMfaChallengeResponse,
  LoginPayload,
  RefreshTokenPayload,
  ResetPasswordPayload,
  ResetPasswordResponse,
  VerifyMfaPayload
} from './types'
import { getDeviceMetadata } from './device'
import { useChatStore } from '../Chat'
import { useMedsStore } from '../Meds'
import { useFaxStore } from '../Fax'
import { useOpportunityStore } from '../Opportunity'

const AUTH_STORAGE_KEY = 'auth-zustand-storage'

// an access token this close to its expiry is refreshed before it is sent;
// wide enough for a hidden tab, whose timers chrome runs once a minute
const REFRESH_MARGIN = 2 * 60 * 1000

// Flipper may emit errors as `{ error }` at the top level or nested in `body`
const toAuthError = (response: any): FlipperAuthError | null => {
  const error = response?.error ?? response?.body?.error
  const errmsg = response?.errmsg ?? response?.body?.errmsg
  if (error || errmsg) {
    return {
      error: error ?? errmsg,
      errmsg: errmsg ?? error,
      code: response?.code
    }
  }
  return null
}

const toSession = (credentials: AuthCredentialsResponse) => ({
  accessToken: credentials.access_token,
  refreshToken: credentials.refresh_token,
  accessTokenExpiresAt: Date.now() + credentials.expires_in * 1000,
  userId: credentials.user_id
})

const emptySession = {
  accessToken: '',
  refreshToken: '',
  accessTokenExpiresAt: null,
  userId: null
}

const normalizeAuthDevice = (device: AuthDevice): AuthDevice => ({
  ...device,
  last_activity_at: toUtcIsoString(device.last_activity_at),
  expires_at: toUtcIsoString(device.expires_at),
  revoked: {
    ...device.revoked,
    at: toUtcIsoString(device.revoked.at ?? undefined) ?? null
  },
  _created: toUtcIsoString(device._created),
  _updated: toUtcIsoString(device._updated)
})

// every tab shares the refresh token, and flipper revokes the device when one
// is used twice, so only one refresh may run at a time across tabs
const withRefreshLock = <T>(task: () => Promise<T>): Promise<T> =>
  navigator.locks
    ? (navigator.locks.request('auth-refresh', task) as Promise<T>)
    : task()

let refreshing: Promise<boolean> | null = null

// the token is the whole session now, so authentication is derived from it
export const selectIsAuthenticated = (state: AuthState) => !!state.accessToken

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        ...emptySession,
        mfaToken: '',
        mfaUserId: null,
        pendingMfa: false,

        // flipper answers a professional with an mfa challenge; the device
        // credentials come from /user/login/mfa/v4 once the code is verified
        login: async (values: LoginCredentials) => {
          try {
            const body: LoginPayload = { ...values, ...getDeviceMetadata() }
            const response = await http.post('/user/login/v4', body, {
              headers: { Authorization: null },
              validateStatus: () => true
            })

            const error = toAuthError(response)
            if (error) {
              return error
            }

            // credentials without a challenge would skip mfa, which a
            // professional must never do
            const mfaResponse: LoginMfaChallengeResponse = response
            if (!mfaResponse?.mfa || !mfaResponse?.mfa_token) {
              return {
                errmsg: 'Could not start the verification. Please try again.'
              }
            }

            set({
              mfaToken: mfaResponse.mfa_token,
              mfaUserId: mfaResponse.user_id,
              pendingMfa: true
            })

            return mfaResponse
          } catch (error) {
            console.error('Error logging in:', error)
            return { errmsg: error?.message }
          }
        },

        verifyMfa: async (value: string) => {
          try {
            const { mfaToken, pendingMfa } = get()
            if (!pendingMfa || !mfaToken) {
              return { errmsg: 'No pending MFA verification' }
            }

            const body: VerifyMfaPayload = {
              value,
              mfa_token: mfaToken,
              ...getDeviceMetadata()
            }
            const response = await http.post('/user/login/mfa/v4', body, {
              headers: { Authorization: null },
              validateStatus: () => true
            })

            const error = toAuthError(response)
            if (error) {
              return error
            }

            const credentials: AuthCredentialsResponse = response
            if (!credentials?.access_token || !credentials?.refresh_token) {
              return { errmsg: 'Could not verify the code. Please try again.' }
            }

            get().clearMfa()
            set(toSession(credentials))
            return credentials
          } catch (error) {
            console.error('Error verifying MFA:', error)
            return { errmsg: error?.message }
          }
        },

        refreshSession: () => {
          const startedWith = get().refreshToken

          refreshing ??= withRefreshLock(async () => {
            // another tab may have rotated the token while this one waited
            // for the lock; its refresh already covers this one
            await useAuthStore.persist.rehydrate()
            const { refreshToken } = get()
            if (!refreshToken) {
              return false
            }
            if (refreshToken !== startedWith) {
              return true
            }

            try {
              const body: RefreshTokenPayload = { refresh_token: refreshToken }
              const credentials: AuthCredentialsResponse = await http.post(
                '/user/login/refreshtoken/v4',
                body,
                { headers: { Authorization: null } }
              )
              set(toSession(credentials))
              return true
            } catch (error) {
              // 401 is a spent, reused, revoked or expired refresh token: the
              // device is gone. Anything else (offline, 5xx) keeps the session
              // for the next attempt
              if (error?.response?.status === 401) {
                get().clearAllZustandStores()
              } else {
                console.error('Error refreshing session:', error)
              }
              return false
            }
          }).finally(() => {
            refreshing = null
          })

          return refreshing
        },

        getFreshAccessToken: async () => {
          const { accessToken, accessTokenExpiresAt } = get()
          if (!accessToken) {
            return ''
          }
          if ((accessTokenExpiresAt ?? 0) - Date.now() > REFRESH_MARGIN) {
            return accessToken
          }

          await get().refreshSession()
          return get().accessToken
        },

        logout: async () => {
          try {
            if (get().accessToken) {
              await http.post('/user/logout/v4')
            }
          } catch (error) {
            // the local session goes either way; a device left active server
            // side still expires with its refresh token
            console.error('Error logging out:', error)
          } finally {
            get().clearAllZustandStores()
          }
        },

        // flipper revokes every device of the professional, this one
        // included, so the session ends once the new password is set
        resetPassword: async (values: ResetPasswordPayload) => {
          try {
            const response: ResetPasswordResponse = await http.post(
              '/professional/password/reset',
              values
            )
            return response
          } catch (error) {
            // a rejected password answers 401 with a plain text body, so the
            // status is the only thing we can read back from the response
            if (error?.response?.status === 401) {
              return { error: 'Invalid password', errmsg: 'Invalid password' }
            }
            console.error('Error resetting password:', error)
            return { errmsg: error?.message }
          }
        },

        fetchAuthDevices: async () => {
          try {
            const devices: AuthDevice[] = await http.get(
              `/user/${get().userId}/auth-devices`
            )
            return (devices ?? []).map(normalizeAuthDevice)
          } catch (error) {
            console.error('Error fetching auth devices:', error)
            return { errmsg: error?.message }
          }
        },

        // revokes this device too, so the session ends with it
        revokeAllAuthDevices: async () => {
          try {
            const response = await http.post(
              `/user/${get().userId}/auth-devices/revoke-all`
            )
            get().clearAllZustandStores()
            return response
          } catch (error) {
            console.error('Error revoking auth devices:', error)
            return { errmsg: error?.message }
          }
        },

        clearMfa: () => {
          set({ mfaToken: '', mfaUserId: null, pendingMfa: false })
        },

        clear: () => {
          set({
            ...emptySession,
            mfaToken: '',
            mfaUserId: null,
            pendingMfa: false
          })
        },

        clearAllZustandStores: () => {
          try {
            get().clear()
            useChatStore.getState().clear()
            useMedsStore.getState().clear()
            useFaxStore.getState().clear()
            useOpportunityStore.getState().clear()

            localStorage.removeItem(AUTH_STORAGE_KEY)
            sessionStorage.removeItem('chat-zustand-storage')
            sessionStorage.removeItem('meds-zustand-storage')
            sessionStorage.removeItem('fax-zustand-storage')
            sessionStorage.removeItem('opportunity-storage')
          } catch (error) {
            console.error('Error clearing zustand stores', error)
          }
        }
      }),
      {
        name: AUTH_STORAGE_KEY,
        // shared across tabs and surviving a restart; localStorage is
        // synchronous, so getState().accessToken is already populated when
        // the first request reads it
        storage: createJSONStorage(() => localStorage),
        partialize: state => ({
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          accessTokenExpiresAt: state.accessTokenExpiresAt,
          userId: state.userId
        }),
        // version 0 held a v3 token with no refresh token, which v4 cannot
        // renew, so those sessions log in again
        version: 1,
        migrate: () => emptySession
      }
    )
  )
)

// the http client reads the session through this provider, so it never has
// to import the store back
registerSessionProvider({
  getAccessToken: () => useAuthStore.getState().getFreshAccessToken(),
  refreshSession: () => useAuthStore.getState().refreshSession()
})

// a login, refresh or logout in another tab lands here: follow its session,
// and drop this tab's data when that session ended
window.addEventListener('storage', async event => {
  if (event.key !== AUTH_STORAGE_KEY) {
    return
  }

  const hadSession = !!useAuthStore.getState().accessToken
  if (event.newValue !== null) {
    await useAuthStore.persist.rehydrate()
  }

  const hasSession =
    event.newValue !== null && !!useAuthStore.getState().accessToken
  if (hadSession && !hasSession) {
    useAuthStore.getState().clearAllZustandStores()
  }
})
